import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { VerificationChallenge } from "../../store/portalAuthApi";
import "./verificationCodeForm.css";

type Props = {
  challenge: VerificationChallenge;
  pending: boolean;
  onSubmit: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onChangeEmail: () => void;
  onCancel?: () => void;
  submitLabel: string;
  children: ComponentChildren;
};

export function VerificationCodeForm({
  challenge,
  pending,
  onSubmit,
  onResend,
  onChangeEmail,
  onCancel,
  submitLabel,
  children,
}: Props) {
  const [code, setCode] = useState("");
  const [resendSeconds, setResendSeconds] = useState(60);
  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const availableAt = performance.now() + 60_000;
    setResendSeconds(60);
    const timer = window.setInterval(() => {
      setResendSeconds(Math.max(0, Math.ceil((availableAt - performance.now()) / 1000)));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [challenge]);

  useEffect(() => {
    if (!pending) codeInput.current?.focus();
  }, [challenge, pending]);

  return (
    <form
      class="verification-code-form"
      aria-busy={pending}
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit(code);
      }}
    >
      {children}
      <label class="form-control">
        <span>Verification code</span>
        <input
          ref={codeInput}
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={code}
          disabled={pending}
          onInput={(event) => setCode(event.currentTarget.value)}
        />
      </label>
      <div class="verification-code-form__actions">
        <button class="primary-action" type="submit" disabled={pending}>
          {pending ? "Verifying…" : submitLabel}
        </button>
        {onCancel && (
          <button class="secondary-action" type="button" disabled={pending} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
      <div class="verification-code-form__actions">
        <button
          class="link-action"
          type="button"
          disabled={pending || resendSeconds > 0}
          onClick={() => {
            setCode("");
            void onResend();
          }}
        >
          {resendSeconds > 0 ? `Resend in ${resendSeconds}s` : "Resend code"}
        </button>
        <button class="link-action" type="button" disabled={pending} onClick={onChangeEmail}>
          Change email
        </button>
      </div>
    </form>
  );
}
